import { ICON } from '../Icons/Icons';
import SpacesSocket from '../../socket/SpacesSocket';
import '../../styles/components/spacesOnlineFriends.scss';

const DEFAULT_AVATAR = '/textures/defaultAvatar.png';

export default class SpacesOnlineFriends {
  constructor(config, socket) {
    this.root = document.getElementById('spaces-online-friends');
    this.config = config || {};
    this.friends = [];
    this.friendIds = new Set();
    this.online = [];
    this.users = new Map();
    this.menu = null;
    this.menuUser = null;
    this.menuButton = null;
    this.loaded = false;
    this.failed = false;
    this.presenceKnown = false;
    this.socket = null;

    if (!this.root || window.isGuest || !this.config.socketUrl) {
      return;
    }

    this.mount();
    this.socket = socket || new SpacesSocket(this.config);
    this.socket.onStatus((connected) => {
      this.root.classList.toggle('is-connected', connected);
    });
    this.socket.onChange((users) => {
      this.presenceKnown = true;
      this.online = this.onlyFriends(users);
      this.renderList();
    });
    if (!socket) {
      this.socket.connect();
    }
    this.loadFriends();
    setTimeout(() => {
      if (!this.presenceKnown) {
        this.presenceKnown = true;
        this.renderList();
      }
    }, 8000);
  }

  mount() {
    this.root.classList.add('spaces-online-friends');

    this.root.innerHTML = `
      <button class="spaces-online-friends__toggle" type="button" aria-expanded="false">
        <span class="material-icons spaces-online-friends__icon" aria-hidden="true">people</span>
        <span class="spaces-online-friends__count">0</span>
        <span class="spaces-online-friends__label" data-lang="spaces-online-friends-title">Friends</span>
      </button>
      <div class="spaces-online-friends__panel">
        <div class="spaces-online-friends__header">
          <span class="spaces-online-friends__heading" data-lang="spaces-online-friends-title">Friends</span>
          <button type="button" class="spaces-online-friends__close" aria-label="Close">${ICON.CLOSE}</button>
        </div>
        <div class="spaces-online-friends__list"></div>
      </div>
    `;

    this.toggle = this.root.querySelector('.spaces-online-friends__toggle');
    this.count = this.root.querySelector('.spaces-online-friends__count');
    this.list = this.root.querySelector('.spaces-online-friends__list');
    this.root.querySelector('.spaces-online-friends__close').addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.close();
    });
    this.toggle.setAttribute('aria-expanded', this.root.classList.contains('is-open') ? 'true' : 'false');

    this.toggle.addEventListener('click', () => {
      const open = this.root.classList.toggle('is-open');
      if (open && typeof this.onOpen === 'function') {
        this.onOpen();
      }
      this.syncToggle(open);
    });

    this.list.addEventListener('click', (event) => {
      const action = event.target.closest('[data-action]');
      if (!action || !this.list.contains(action)) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      const row = action.closest('.spaces-online-friends__item');
      const user = row && this.users.get(row.dataset.userId);
      if (!user) {
        return;
      }
      if (action.dataset.action === 'chat') {
        this.openChat(user);
      } else if (action.dataset.action === 'scene') {
        this.goToScene(user);
      } else if (action.dataset.action === 'menu') {
        this.toggleMenu(action, user);
      }
    });

    this.list.addEventListener('scroll', () => this.closeMenu());
    document.addEventListener('click', (event) => {
      const target = event.target;
      const inMenu = !!(this.menu && this.menu.contains(target));
      if (this.menu && !this.menu.hidden && !inMenu) {
        this.closeMenu();
      }
      if (!this.root.classList.contains('is-open')) {
        return;
      }
      if (this.root.contains(target) || inMenu) {
        return;
      }
      const modal = document.getElementById('guestAndOtherUserProfileModal');
      if (modal && modal.contains(target)) {
        return;
      }
      this.close();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.closeMenu();
        const modal = document.getElementById('guestAndOtherUserProfileModal');
        if (modal) {
          modal.classList.remove('is-active');
        }
      }
    });

    this.renderList();
  }

  syncToggle(open) {
    this.toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    document.body.classList.toggle('spaces-sheet-open', open);
  }

  close() {
    this.closeMenu();
    this.root.classList.remove('is-open');
    this.syncToggle(false);
  }

  loadFriends() {
    fetch('/rest/get-friends', {
      method: 'GET',
      credentials: 'same-origin',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json',
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('friends request failed');
        }
        return response.json();
      })
      .then((data) => {
        const friends = Array.isArray(data) ? data : (data && data.data) || [];
        this.friends = friends;
        this.friendIds = new Set(friends.map((friend) => String(friend.id)));
        this.loaded = true;
        this.failed = false;
        this.socket.setFriendIds(friends.map((friend) => friend.id));
        this.online = this.onlyFriends(this.socket.onlineUsers);
        this.renderList();
      })
      .catch(() => {
        this.loaded = true;
        this.failed = true;
        this.renderList();
      });
  }

  isOnline(userId) {
    return this.online.some((user) => String(user.userId) === String(userId));
  }

  onlyFriends(users) {
    return (users || []).filter((user) => this.friendIds.has(String(user.userId)));
  }

  renderList() {
    if (!this.list) {
      return;
    }

    this.closeMenu();
    this.users.clear();
    this.count.textContent = String(this.online.length);
    this.list.replaceChildren();

    if (!this.loaded) {
      this.list.appendChild(this.status('spaces-online-friends-loading', 'Loading friends...'));
      this.applyLanguage();
      return;
    }

    if (this.failed) {
      this.list.appendChild(this.status('spaces-online-friends-error', 'Could not load friends'));
      this.applyLanguage();
      return;
    }

    if (!this.friends.length) {
      this.list.appendChild(this.status('spaces-online-friends-none', 'You have no friends yet'));
      this.applyLanguage();
      return;
    }

    if (!this.presenceKnown) {
      this.list.appendChild(this.status('spaces-online-friends-loading', 'Loading friends...'));
      this.applyLanguage();
      return;
    }

    const onlineIds = new Set(this.online.map((user) => String(user.userId)));
    const offline = this.friends
      .filter((friend) => !onlineIds.has(String(friend.id)))
      .sort(byFriendName);

    this.online.slice().sort(bySocketName).forEach((user) => {
      this.list.appendChild(this.friendRow(user, true));
    });

    if (!this.online.length) {
      this.list.appendChild(this.status('spaces-online-friends-empty', 'No friends online'));
    }

    if (offline.length) {
      this.list.appendChild(this.section('spaces-friends-offline', 'Offline'));
      offline.forEach((friend) => {
        this.list.appendChild(this.friendRow({
          userId: friend.id,
          userName: friend.username,
          userAvatarUrl: friend.avatar_image_url,
        }, false));
      });
    }

    this.applyLanguage();
  }

  section(langKey, fallback) {
    const node = document.createElement('p');
    node.className = 'spaces-online-friends__section';
    node.setAttribute('data-lang', langKey);
    node.textContent = fallback;
    return node;
  }

  status(langKey, fallback) {
    const node = document.createElement('p');
    node.className = 'spaces-online-friends__status';
    node.setAttribute('data-lang', langKey);
    node.textContent = fallback;
    return node;
  }

  friendRow(user, online) {
    const userId = String(user.userId);
    this.users.set(userId, user);

    const row = document.createElement('div');
    row.className = `spaces-online-friends__item${online ? '' : ' spaces-online-friends__item--offline'}`;
    row.dataset.userId = userId;
    row.dataset.online = online ? '1' : '0';

    const avatar = document.createElement('img');
    avatar.className = 'spaces-online-friends__avatar';
    avatar.alt = '';
    avatar.src = user.userAvatarUrl || user.avatar_image_url || DEFAULT_AVATAR;
    avatar.addEventListener('error', function onAvatarError() {
      avatar.removeEventListener('error', onAvatarError);
      if (avatar.getAttribute('src') === DEFAULT_AVATAR) {
        return;
      }
      avatar.src = DEFAULT_AVATAR;
    });

    const meta = document.createElement('span');
    meta.className = 'spaces-online-friends__meta';

    const name = document.createElement('span');
    name.className = 'spaces-online-friends__name';
    name.textContent = user.userName || user.username || 'Friend';

    meta.appendChild(name);

    const place = online ? friendPlace(user) : '';
    if (place) {
      const location = document.createElement('span');
      location.className = 'spaces-online-friends__place';
      location.textContent = place;
      meta.appendChild(location);
    } else if (!online) {
      const location = document.createElement('span');
      location.className = 'spaces-online-friends__place';
      location.setAttribute('data-lang', 'spaces-friends-offline');
      location.textContent = 'Offline';
      meta.appendChild(location);
    }

    const actions = document.createElement('span');
    actions.className = 'spaces-online-friends__actions';
    actions.appendChild(iconButton('chat', 'Chat', ICON.MESSAGE));
    if (viewSpaceUrl(user)) {
      actions.appendChild(iconButton('scene', 'Go to scene', ICON.GO_TO_SCENE));
    }
    const menuButton = iconButton('menu', 'More', ICON.THREE_DOTS);
    menuButton.setAttribute('aria-haspopup', 'menu');
    menuButton.setAttribute('aria-expanded', 'false');
    actions.appendChild(menuButton);

    row.appendChild(avatar);
    row.appendChild(meta);
    row.appendChild(actions);
    return row;
  }

  openChat(user) {
    if (typeof this.onChat !== 'function') {
      return;
    }
    this.onChat({
      id: user.userId,
      username: user.userName || user.username || 'Friend',
      avatar: user.userAvatarUrl || user.avatar_image_url || DEFAULT_AVATAR,
    });
  }

  goToScene(user) {
    const url = viewSpaceUrl(user);
    if (!url) {
      return;
    }
    window.location.assign(url);
  }

  startVideoCall(user) {
    if (typeof this.onVideoCall === 'function') {
      this.close();
      this.onVideoCall(user);
      return;
    }
    const url = viewSpaceUrl(user);
    if (!url) {
      return;
    }
    const destination = new URL(url, window.location.origin);
    destination.searchParams.set('peerAction', 'call');
    destination.searchParams.set('peerId', String(user.userId));
    window.location.assign(destination.toString());
  }

  ensureMenu() {
    if (this.menu) {
      return;
    }
    this.menu = document.createElement('div');
    this.menu.className = 'spaces-online-friends__menu';
    this.menu.hidden = true;
    this.menu.setAttribute('role', 'menu');
    this.menu.innerHTML = `
      <button type="button" role="menuitem" data-action="remove" data-lang="remove-from-friends-text">Remove from friends</button>
      <button type="button" role="menuitem" data-action="profile" data-lang="show-user-info-text">View profile</button>
      <button type="button" role="menuitem" data-action="call" data-lang="video-call-text">Start video call</button>
      <button type="button" role="menuitem" data-action="jump" data-lang="jump-to-scene-text">Jump to scene</button>
    `;
    document.body.appendChild(this.menu);
    this.menu.addEventListener('click', (event) => {
      const item = event.target.closest('[data-action]');
      if (!item || item.disabled) {
        return;
      }
      const user = this.menuUser;
      const row = this.menuButton && this.menuButton.closest('.spaces-online-friends__item');
      const online = !!(row && row.dataset.online === '1');
      this.closeMenu();
      if (!user) {
        return;
      }
      if (item.dataset.action === 'remove') {
        this.removeFriend(user);
      } else if (item.dataset.action === 'profile') {
        this.viewProfile(user, online);
      } else if (item.dataset.action === 'call') {
        this.startVideoCall(user);
      } else if (item.dataset.action === 'jump') {
        this.goToScene(user);
      }
    });
    this.applyLanguage();
  }

  toggleMenu(button, user) {
    this.ensureMenu();
    if (!this.menu.hidden && this.menuUser && String(this.menuUser.userId) === String(user.userId)) {
      this.closeMenu();
      return;
    }
    this.closeMenu();
    this.menuUser = user;
    const canVisit = !!viewSpaceUrl(user);
    const online = button.closest('.spaces-online-friends__item')?.dataset.online === '1';
    this.menu.querySelector('[data-action="call"]').disabled = !online;
    this.menu.querySelector('[data-action="jump"]').disabled = !canVisit;
    this.menu.hidden = false;
    const rect = button.getBoundingClientRect();
    const menuRect = this.menu.getBoundingClientRect();
    let top = rect.bottom + 4;
    if (top + menuRect.height > window.innerHeight - 8) {
      top = Math.max(8, rect.top - menuRect.height - 4);
    }
    let left = rect.right - menuRect.width;
    left = Math.max(8, Math.min(left, window.innerWidth - menuRect.width - 8));
    this.menu.style.top = `${top}px`;
    this.menu.style.left = `${left}px`;
    button.setAttribute('aria-expanded', 'true');
    this.menuButton = button;
  }

  closeMenu() {
    if (this.menuButton) {
      this.menuButton.setAttribute('aria-expanded', 'false');
    }
    this.menuButton = null;
    this.menuUser = null;
    if (this.menu) {
      this.menu.hidden = true;
    }
  }

  removeFriend(user) {
    const userId = user.userId;
    fetch('/rest/remove-friend', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify({ user_id: userId }),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('remove friend failed');
        }
        this.friends = this.friends.filter((friend) => String(friend.id) !== String(userId));
        this.friendIds.delete(String(userId));
        this.socket.setFriendIds(this.friends.map((friend) => friend.id));
        this.online = this.onlyFriends(this.online);
        this.renderList();
      })
      .catch(() => {});
  }

  viewProfile(user, online) {
    const modal = document.getElementById('guestAndOtherUserProfileModal');
    if (!modal) {
      return;
    }
    this.bindProfileModal(modal);
    setText('peer_info_username', user.userName || user.username || '');
    setText('peer_status_text', online ? 'Available' : 'Offline');
    setText('peer_info_location', friendPlace(user));
    setText('peer_info_interests', '');
    setText('peer_info_user_plan', '');
    const avatar = document.getElementById('peer_info_avatar');
    if (avatar) {
      avatar.src = user.userAvatarUrl || user.avatar_image_url || DEFAULT_AVATAR;
    }
    const friendIcon = document.getElementById('peer_is_friend');
    if (friendIcon) {
      friendIcon.style.display = '';
    }
    modal.classList.add('is-active');

    const requestId = String(user.userId);
    this.profileRequest = requestId;
    fetch(`/profile/info/${encodeURIComponent(user.userId)}`, {
      method: 'GET',
      credentials: 'same-origin',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json',
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('profile request failed');
        }
        return response.json();
      })
      .then((profile) => {
        if (this.profileRequest !== requestId || !profile) {
          return;
        }
        setText('peer_info_username', profile.username || user.userName || user.username || '');
        if (avatar && profile.avatar_image_url) {
          avatar.src = profile.avatar_image_url;
        }
        const place = profile.city || friendPlace(user);
        setText('peer_info_location', place);
        setText('peer_info_interests', profile.interests || '');
        const plan = profile.subscription && (profile.subscription.name || profile.subscription);
        setText('peer_info_user_plan', typeof plan === 'string' ? plan : '');
      })
      .catch(() => {});
  }

  bindProfileModal(modal) {
    if (modal.dataset.spacesBound) {
      return;
    }
    modal.dataset.spacesBound = '1';
    const close = () => modal.classList.remove('is-active');
    const closeButton = modal.querySelector('.close-modal');
    if (closeButton) {
      closeButton.addEventListener('click', close);
    }
    const background = modal.querySelector('.modal-background');
    if (background) {
      background.addEventListener('click', close);
    }
  }

  applyLanguage() {
    if (window.multiLanguagesController && typeof window.multiLanguagesController.renderLanguage === 'function') {
      window.multiLanguagesController.renderLanguage();
    }
    this.applyMenuLabels();
  }

  applyMenuLabels() {
    if (!this.menu) {
      return;
    }
    const language = window.multiLanguagesController && window.multiLanguagesController.language;
    if (language && language !== 'en') {
      return;
    }
    const labels = {
      remove: 'Remove from friends',
      profile: 'View profile',
      call: 'Start video call',
      jump: 'Jump to scene',
    };
    Object.keys(labels).forEach((action) => {
      const button = this.menu.querySelector(`[data-action="${action}"]`);
      if (button) {
        button.textContent = labels[action];
      }
    });
  }
}

function iconButton(action, label, icon) {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'spaces-online-friends__action';
  button.dataset.action = action;
  button.setAttribute('aria-label', label);
  button.innerHTML = icon;
  return button;
}

function setText(id, value) {
  const node = document.getElementById(id);
  if (node) {
    node.textContent = value || '';
  }
}

function friendPlace(user) {
  if (isOnSpacesPage(user)) {
    return '';
  }
  return [user.worldName, user.currentSceneName].filter(Boolean).join(' · ');
}

function isOnSpacesPage(user) {
  if (!user) {
    return false;
  }
  if (String(user.currentSceneName || '').trim().toLowerCase() === 'spaces') {
    return true;
  }
  if (Number(user.worldId) === 101010) {
    return true;
  }
  const path = sameOriginPath(user.url);
  if (!path) {
    return false;
  }
  try {
    const pathname = new URL(path, window.location.origin).pathname.replace(/\/+$/, '') || '/';
    return pathname === '/spaces';
  } catch (error) {
    return false;
  }
}

function viewSpaceUrl(user) {
  const path = sameOriginPath(user && user.url);
  if (!path) {
    return '';
  }
  try {
    const parsed = new URL(path, window.location.origin);
    const route = parsed.searchParams.get('r') || '';
    const isView = parsed.pathname.indexOf('/worlds/view') !== -1 || route.indexOf('worlds/view') !== -1;
    if (!isView) {
      return '';
    }
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch (error) {
    return '';
  }
}

function bySocketName(a, b) {
  return String(a.userName || a.username || '').localeCompare(String(b.userName || b.username || ''));
}

function byFriendName(a, b) {
  return String(a.username || '').localeCompare(String(b.username || ''));
}

function sameOriginPath(url) {
  if (!url || typeof url !== 'string') {
    return '';
  }
  try {
    const parsed = new URL(url, window.location.origin);
    if (parsed.origin !== window.location.origin) {
      return '';
    }
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch (error) {
    return '';
  }
}
